import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { FileTypes } from '@guryou/core';
import {
  Block,
  DefaultText,
  FlexColumn,
  FlexRow,
  HtmlVideoPlayer,
  ImageMedium,
  imageNotFound,
  LargeText,
  MediumText,
  Modal,
  SmallGrayText,
  Spinner,
} from '@guryou/html-components';
import {
  getUserVideoCourseFilesFeel,
  PROVIDER_GET_GENERAL_INFO,
  PROVIDER_LATEST_WIDGET,
  resetGetFileState,
  resetState,
  stateIsLoaded,
  stateIsLoading,
  useEffectOnSuccess,
} from '@guryou/redux';
import { Hidden, makeStyles } from '@material-ui/core';
import MyCustomPreviewModal from './MyCustomPreviewModal';
import MyImagePreview from './MyImagePreview';
import VideoCourseAccordion from './VideoCourseAccordion';
import './my-custom-style.css';

const useStyles = makeStyles(() => ({
  span: {
    cursor: 'pointer',
    '&:hover': {
      color: '#B3B3B3',
    },
  },
}));

export const VideoCourseView = ({ vcSelector, videoCourse, vfiles, allowPreview }) => {
  const spanClasses = useStyles();
  const { i18n, t } = useTranslation();
  const [imageSrc, setImageSrc] = useState(videoCourse?.imageUrl);

  const history = useHistory();
  const dispatch = useDispatch();

  useEffectOnSuccess(
    state => state.files.getPublic,
    data => {
      if (data?.type === FileTypes.document) {
        window.open(data?.signedUrl, '_blank');
      }
      dispatch(resetGetFileState());
    }
  );

  const handleError = () => {
    setImageSrc(imageNotFound); // Set to default image if the original fails
  };

  const handleLearnMore = () => {
    const providerId = videoCourse?.providerId;
    if (providerId) {
      dispatch(resetState(PROVIDER_GET_GENERAL_INFO));
      dispatch(resetState(PROVIDER_LATEST_WIDGET));
      return history.push(`/${i18n.language}/provider/${providerId}`);
    }
  };

  return (
    <>
      <FlexRow margin={0} paddingTop={20} paddingBottom={20}>
        <FlexRow margin={0} marginTop="3rem" height="auto" padding={20} backgroundColor="#222222" alignItems="center">
          <Block color="white" lg={7} md={12} xs={12} margin={0} display="flex" alignItems="center" justifyContent="center">
            <FlexColumn height="100%" alignItems="start" justifyContent="start">
              <LargeText fontWeight="800" marginBottom={4} color="white">
                {videoCourse?.name}
              </LargeText>
              <div className="my-parent" dangerouslySetInnerHTML={{ __html: videoCourse?.description }}></div>
              <SmallGrayText>
                {t('VIDEO_COURSE_PAGE.createdBy')}
                <span className={spanClasses.span} onClick={handleLearnMore}>
                  {videoCourse?.providerName}
                </span>
              </SmallGrayText>
            </FlexColumn>
          </Block>
          <Hidden mdDown>
            <Block lg={5} md={12} xs={12} margin={0}>
              <ImageMedium
                src={imageSrc || videoCourse?.imageUrl}
                maxWidth="400px"
                onError={handleError}
                border="1px solid white"
                borderRadius="10px"
              ></ImageMedium>
            </Block>
          </Hidden>
        </FlexRow>
      </FlexRow>
      <Block padding={10}>
        <MediumText marginBottom={5}>{t('VIDEO_COURSE_PAGE.courseContent')}</MediumText>
        {stateIsLoading(vcSelector) ? (
          <FlexRow xs={12} justifyContent="center" alignItems="center" height="20vh">
            <Spinner />
          </FlexRow>
        ) : (
          <FlexRow lg={8} md={12} xs={12} margin={0}>
            {vfiles.length ? (
              vfiles?.map(section => <VideoCourseAccordion key={section.sectionId} section={section} allowPreview={allowPreview} />)
            ) : (
              <DefaultText>{t('VIDEO_COURSE_PAGE.courseEmpty')}</DefaultText>
            )}
          </FlexRow>
        )}
      </Block>
    </>
  );
};

const UserVideoCourseFiles = ({ id, videoCourse }) => {
  const dispatch = useDispatch();
  const vcSelector = useSelector(state => state.videoCourses.userVideoCourseFiles);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) {
      dispatch(getUserVideoCourseFilesFeel(id));
    }
  }, [dispatch, id]);

  if (!id || (stateIsLoaded(vcSelector) && !vcSelector.data)) {
    return (
      <FlexRow height="100vh">
        <MediumText>Oops, something went wrong!</MediumText>
      </FlexRow>
    );
  }
  const vfiles = (stateIsLoaded(vcSelector) && vcSelector?.data?.length && vcSelector.data) || [];

  return (
    <>
      <VideoCourseView vfiles={vfiles} videoCourse={videoCourse} vcSelector={vcSelector} allowPreview={true} />
      <Modal id="playVideoMedia" maxWidth="md">
        <HtmlVideoPlayer />
      </Modal>
      <Modal id="previewImage" maxWidth="lg">
        <MyImagePreview />
      </Modal>

      <Modal id="myCustomModal" maxWidth="lg">
        <MyCustomPreviewModal />
      </Modal>
    </>
  );
};

export default UserVideoCourseFiles;
